import { type FC, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { EyeIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useRouteLoaderData } from '@remix-run/react';
import { destinationKey } from '@shared/dream-components';
import cx from 'classnames';

import type { loader as communityLayoutLoader } from '../../../community';
import { useMovePostMutation } from '../../_hooks/useMovePostMutation';
import { ChannelHintIcon } from './ChannelHintIcon';
import { ConfirmDialog } from './ConfirmDialog';
import { UnpinWarning } from './UnpinWarning';

const FEED_KEY = destinationKey({ kind: 'feed' });

export interface MoveTarget {
  postId: string;
  currentSpaceSlug: string | null;
  isPinned: boolean;
}

interface MovePostDialogProps {
  isOpen: boolean;
  target: MoveTarget | null;
  onMoved: () => void;
  onCancel: () => void;
  onClosed?: () => void;
}

export const MovePostDialog: FC<MovePostDialogProps> = ({ isOpen, target, onMoved, onCancel, onClosed }) => {
  const { t } = useTranslation();
  const layoutData = useRouteLoaderData<typeof communityLayoutLoader>('routes/community');
  const moveMutation = useMovePostMutation();
  const [selectedKey, setSelectedKey] = useState('');

  // Read only channels stay listed but disabled: hiding them reads as a missing
  // channel, while a disabled row with its reason explains why it can't be picked.
  const destinations = useMemo(
    () => (layoutData?.spaces ?? []).filter((space) => space.slug !== target?.currentSpaceSlug),
    [layoutData?.spaces, target?.currentSpaceSlug],
  );
  const showFeed = layoutData?.community?.feed_first_enabled === true && target?.currentSpaceSlug != null;

  const currentChannel = layoutData?.spaces?.find((space) => space.slug === target?.currentSpaceSlug);

  const selected = destinations.find((space) => destinationKey({ kind: 'channel', space }) === selectedKey);
  const feedSelected = showFeed && selectedKey === FEED_KEY;
  const gatedHint = t('community.action_menu.move_post_gated_warning');
  const readOnlyHint = t('community.action_menu.move_post_read_only_hint');
  const isLoading = moveMutation.isPending;

  const handleConfirm = () => {
    if (!target) return;

    if (feedSelected) {
      moveMutation.mutate(
        {
          postId: target.postId,
          spaceSlug: null,
          spaceName: t('community.composer.destination_feed'),
        },
        { onSuccess: onMoved },
      );
      return;
    }

    if (!selected) return;

    moveMutation.mutate(
      { postId: target.postId, spaceSlug: selected.slug, spaceName: selected.name },
      // Stay open (with the spinner) until the request settles, so a failure leaves
      // a retry path instead of a dismissed dialog.
      { onSuccess: onMoved },
    );
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={t('community.action_menu.move_post_title')}
      description={t('community.action_menu.move_post_description')}
      confirmLabel={t('community.action_menu.move_post_confirm')}
      onConfirm={handleConfirm}
      onCancel={onCancel}
      afterLeave={() => {
        // Cleared on close (not via a remount key) so the dialog keeps its instance
        // through the fade-out and opens with no channel preselected next time.
        setSelectedKey('');
        onClosed?.();
      }}
      confirmDisabled={!selected && !feedSelected}
      isLoading={isLoading}
      body={
        <div className="mt-4 flex flex-col gap-3">
          {currentChannel && (
            <div className="flex items-center gap-1.5 text-sm text-black/60">
              <span className="min-w-0 truncate">
                <Trans
                  i18nKey="community.action_menu.move_post_current_channel"
                  values={{ channel: currentChannel.name }}
                  components={{ strong: <strong className="font-medium text-black" /> }}
                />
              </span>
              {currentChannel.gated && (
                <ChannelHintIcon
                  Icon={LockClosedIcon}
                  hint={
                    currentChannel.premium_tier_ids.length > 0
                      ? t('community.action_menu.move_post_gated_source_warning')
                      : t('community.action_menu.move_post_condition_source_warning')
                  }
                  interactive
                />
              )}
            </div>
          )}

          <div
            role="radiogroup"
            aria-label={t('community.action_menu.move_post_description')}
            className="flex max-h-64 flex-col overflow-y-auto rounded-xl border border-black/10"
          >
            {destinations.length === 0 && !showFeed && (
              <p className="px-3 py-3 text-sm text-black/50">{t('community.action_menu.move_post_no_destinations')}</p>
            )}

            {showFeed && (
              <label className="flex cursor-pointer items-center gap-3 border-b border-black/5 px-3 py-3 text-sm text-black last:border-b-0 hover:bg-black/[0.03]">
                <input
                  type="radio"
                  name="move-post-destination"
                  value={FEED_KEY}
                  checked={feedSelected}
                  onChange={() => setSelectedKey(FEED_KEY)}
                  disabled={isLoading}
                  className="size-4 shrink-0 border-black/20 text-black focus:ring-black/20"
                />
                <span className="truncate">{t('community.composer.destination_feed')}</span>
              </label>
            )}

            {destinations.map((space) => {
              // Read only wins over gating: it's why the row can't be picked at all,
              // so the access caveat on top would only add noise.
              const channelKey = destinationKey({ kind: 'channel', space });
              const indicator = space.read_only
                ? { Icon: EyeIcon, hint: readOnlyHint }
                : space.gated
                  ? { Icon: LockClosedIcon, hint: gatedHint }
                  : null;

              return (
                <label
                  key={space.id}
                  className={cx(
                    'flex items-center gap-3 border-b border-black/5 px-3 py-3 text-sm last:border-b-0',
                    space.read_only
                      ? 'cursor-not-allowed text-black/40'
                      : 'cursor-pointer text-black hover:bg-black/[0.03]',
                  )}
                >
                  <input
                    type="radio"
                    name="move-post-destination"
                    value={channelKey}
                    checked={selectedKey === channelKey}
                    onChange={() => setSelectedKey(channelKey)}
                    disabled={isLoading || space.read_only}
                    className="size-4 shrink-0 border-black/20 text-black focus:ring-black/20"
                  />
                  <span className="truncate">{space.name}</span>
                  {indicator && <ChannelHintIcon Icon={indicator.Icon} hint={indicator.hint} />}
                </label>
              );
            })}
          </div>

          {target?.isPinned && <UnpinWarning />}
        </div>
      }
    />
  );
};
